// Shared layout for the Olene sign-in / create-account screens, in PayClear's
// look: white card on the app's gray-50 background, green-600 primary.
// Layout and copy follow olene-platform/docs/signin.md.
import { useState } from "react";
import { APP_NAME } from "../auth/messages";
import { siteUrl } from "../hosts";
import { IS_DEV, PROD_APP_URL } from "../stage";

export function OleneAuthCard({ title, children, footer }) {
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-[400px]">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 px-6 py-8 sm:px-8">
          <a href={siteUrl("/")} className="block mb-6" aria-label={`${APP_NAME} home`}>
            <img src="/assets/images/Payclear800.png" alt={APP_NAME} className="h-9 w-auto" />
          </a>
          <h1 className="text-2xl font-bold text-gray-900 mb-5">{title}</h1>
          {IS_DEV && <DevBanner />}
          {children}
        </div>
        {footer}
      </div>
    </div>
  );
}

/** Dev sites are for Olene staff (olene-platform/docs/signin.md). Amber, never the brand color. */
export function DevBanner() {
  return (
    <div role="note" className="rounded-xl bg-amber-50 border border-amber-300 text-amber-900 text-sm px-4 py-3 mb-4">
      <p className="font-semibold">Development site: for Olene staff only.</p>
      <a href={PROD_APP_URL} className="underline font-medium">Go to {APP_NAME}</a>
    </div>
  );
}

export function OleneBubble({
  body = "Sign in with the same email and password you use for your other Olene apps.",
}) {
  return (
    <div role="note" className="rounded-xl bg-green-50 border border-green-200 p-4 mb-6 flex items-start gap-2">
        <svg className="w-5 h-5 text-green-600 shrink-0" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
        </svg>
      <p className="text-sm text-green-900">{body}</p>
    </div>
  );
}

export function Field({ id, label, hint, inputRef, ...props }) {
  return (
    <div className="mb-4">
      <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <input
        id={id}
        ref={inputRef}
        className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-gray-900 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
        {...props}
      />
      {hint && <p className="text-xs text-gray-500 mt-1">{hint}</p>}
    </div>
  );
}

export function PasswordField({ id, label, hint, inputRef, ...props }) {
  const [shown, setShown] = useState(false);
  return (
    <div className="mb-4">
      <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <div className="relative">
        <input
          id={id}
          ref={inputRef}
          type={shown ? "text" : "password"}
          className="w-full rounded-lg border border-gray-300 pl-3 pr-11 py-2.5 text-gray-900 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
          {...props}
        />
        <button
          type="button"
          onClick={() => setShown((s) => !s)}
          aria-label={shown ? "Hide password" : "Show password"}
          aria-controls={id}
          className="absolute inset-y-0 right-0 px-3 text-gray-400 hover:text-gray-600"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24" aria-hidden="true">
            {shown ? (
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 3l18 18M10.6 10.6a2 2 0 002.8 2.8M9.9 5.1A9.8 9.8 0 0112 5c5 0 9 4.5 10 7-.4 1-1.2 2.3-2.4 3.5M6.2 6.2C3.9 7.6 2.5 9.8 2 12c1 2.5 5 7 10 7 1.8 0 3.4-.6 4.8-1.4" />
            ) : (
              <>
                <path strokeLinecap="round" strokeLinejoin="round" d="M2 12c1-2.5 5-7 10-7s9 4.5 10 7c-1 2.5-5 7-10 7S3 14.5 2 12z" />
                <circle cx="12" cy="12" r="3" />
              </>
            )}
          </svg>
        </button>
      </div>
      {hint && <p className="text-xs text-gray-500 mt-1">{hint}</p>}
    </div>
  );
}

export function PrimaryButton({ busy, busyLabel, children, ...props }) {
  return (
    <button
      type="submit"
      disabled={busy}
      className="w-full rounded-lg bg-green-600 hover:bg-green-700 disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold py-2.5 transition"
      {...props}
    >
      {busy ? busyLabel : children}
    </button>
  );
}

export function Alert({ children }) {
  if (!children) return null;
  return (
    <div role="alert" className="mb-4 rounded-lg bg-red-50 border border-red-200 text-red-800 text-sm px-3 py-2">
      {children}
    </div>
  );
}

export function Notice({ children }) {
  if (!children) return null;
  return (
    <div role="status" className="mb-4 rounded-lg bg-green-50 border border-green-200 text-green-900 text-sm px-3 py-2">
      {children}
    </div>
  );
}

export function TextLink({ children, ...props }) {
  return (
    <button type="button" className="text-sm font-medium text-green-700 hover:text-green-800 hover:underline" {...props}>
      {children}
    </button>
  );
}
